import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Upload, Film, X } from "lucide-react";
import { formatFileSize } from "@/utils/video-compression-simple";

interface UploadVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: any | null;
  onSave: (bookingId: number, videoFile: File, setlist: string) => void;
  isUploading?: boolean;
  uploadProgress?: number;
}

export const UploadVideoModal: React.FC<UploadVideoModalProps> = ({
  isOpen,
  onClose,
  booking,
  onSave,
  isUploading,
  uploadProgress = 0
}) => {
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [setlist, setSetlist] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (booking) {
      setSetlist(booking.setlist || "");
      setVideoFile(null);
    }
  }, [booking]);

  const handleSave = () => {
    if (!videoFile) {
      toast({
        title: "No file selected",
        description: "Please select a video file to upload.",
        variant: "destructive",
      });
      return;
    }

    if (!booking) {
      toast({
        title: "No booking selected",
        description: "Please select a booking first.",
        variant: "destructive",
      });
      return;
    }

    onSave(booking.id, videoFile, setlist);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("video/")) {
      setVideoFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Upload video</DialogTitle>
          <DialogDescription>
            Upload the recording for this booking.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Drop zone */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
              disabled={isUploading}
            />

            {!videoFile ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                disabled={isUploading}
                className={`w-full rounded-xl border-2 border-dashed p-8 flex flex-col items-center gap-3 transition-all cursor-pointer ${
                  isDragging
                    ? "border-white/30 bg-white/[0.06]"
                    : "border-white/[0.08] bg-white/[0.02] hover:border-white/15 hover:bg-white/[0.04]"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className="h-10 w-10 rounded-full bg-white/[0.06] flex items-center justify-center">
                  <Upload className="h-5 w-5 text-white/40" />
                </div>
                <div className="text-center">
                  <p className="text-[14px] text-white/60">
                    Drop your video here or <span className="text-white/90 underline underline-offset-2">browse</span>
                  </p>
                  <p className="text-[12px] text-white/25 mt-1">MP4, MOV, WebM</p>
                </div>
              </button>
            ) : (
              <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-violet-500/15 flex items-center justify-center shrink-0">
                  <Film className="h-5 w-5 text-violet-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] text-white/80 truncate">{videoFile.name}</p>
                  <p className="text-[12px] text-white/30">{formatFileSize(videoFile.size)}</p>
                </div>
                {!isUploading && (
                  <button
                    onClick={() => setVideoFile(null)}
                    className="h-7 w-7 rounded-full flex items-center justify-center text-white/30 hover:text-white/60 hover:bg-white/[0.06] transition-colors"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Setlist */}
          <div className="space-y-2">
            <Label htmlFor="setlist" className="text-[14px] text-white/50">Setlist (optional)</Label>
            <Textarea
              id="setlist"
              placeholder={"Track 1 \u2013 Artist Name\nTrack 2 \u2013 Artist Name\n..."}
              value={setlist}
              onChange={(e) => setSetlist(e.target.value)}
              rows={4}
              disabled={isUploading}
              className="text-[14px] resize-none"
            />
          </div>

          {/* Upload progress */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[13px]">
                <span className="text-white/50">Uploading...</span>
                <span className="text-white/70 tabular-nums">{Math.round(uploadProgress)}%</span>
              </div>
              <Progress value={uploadProgress} />
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" disabled={isUploading} className="text-white/40 hover:text-white">
              Cancel
            </Button>
          </DialogClose>
          <Button
            onClick={handleSave}
            disabled={isUploading || !videoFile}
            className="bg-white hover:bg-white/90 text-black"
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              "Upload"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
